import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { hcApi, hcError } from './api';
import { useHelpConnect } from './HelpConnectAuth';
import { HcAuthShell } from './HcAuthShell';
import { HcInput, HcPrimaryButton } from './HcLoginPage';

export function HcSetPasswordPage({ mode }) {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { signIn } = useHelpConnect();
  const [info, setInfo] = useState(null);
  const [linkError, setLinkError] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setLinkError('This link is missing its security code. Ask your case manager for a new one.'); return; }
    hcApi.get('/auth/token-info', { params: { token } })
      .then(({ data }) => setInfo(data))
      .catch((err) => setLinkError(hcError(err)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw !== pw2) return setError('The passwords do not match.');
    setLoading(true);
    try {
      const { data } = await hcApi.post('/auth/set-password', { token, password: pw });
      await signIn(data.access_token);
      navigate('/helpconnect', { replace: true });
    } catch (err) {
      setError(hcError(err));
    } finally {
      setLoading(false);
    }
  };

  const isSetup = (info?.purpose || mode) === 'setup';
  return (
    <HcAuthShell showPillars={false}
      title={isSetup ? `Welcome${info?.first_name ? `, ${info.first_name}` : ''}!` : 'Reset your password'}
      subtitle={isSetup ? "Let's set up your HELPConnect account." : 'Choose a new password for HELPConnect.'}
      intro="Your password must be at least 8 characters and include a letter and a number.">
      {!info && !linkError && <Loader2 className="w-6 h-6 animate-spin text-[#922A8C]" />}
      {linkError && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-red-700" data-testid="hc-link-error">
          {linkError}
          <div className="mt-3"><Link to="/helpconnect/login" className="font-semibold text-[#922A8C] underline">Go to sign in</Link></div>
        </div>
      )}
      {info && (
        <form onSubmit={submit} className="space-y-4" data-testid="hc-set-password-form">
          <HcInput icon={Mail} type="email" value={info.email || ''} readOnly testId="hc-set-password-email" />
          <HcInput icon={Lock} type="password" reveal placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} required autoComplete="new-password" testId="hc-new-password" />
          <HcInput icon={Lock} type="password" reveal placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} required autoComplete="new-password" testId="hc-confirm-password" />
          {error && <p className="text-sm text-red-600" role="alert" data-testid="hc-set-password-error">{error}</p>}
          <HcPrimaryButton type="submit" loading={loading} data-testid="hc-set-password-submit">
            {isSetup ? 'Create My Account' : 'Save New Password'}
          </HcPrimaryButton>
        </form>
      )}
    </HcAuthShell>
  );
}

export function HcForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await hcApi.post('/auth/forgot-password', { email });
      setSent(data.message);
    } catch (err) {
      setError(hcError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <HcAuthShell showPillars={false} title="Forgot your password?" subtitle="We'll send you a reset link."
      intro="Enter the email you use for HELPConnect. We'll text and email you a single-use link to choose a new password. You can also ask your case manager for a link.">
      {sent ? (
        <div className="rounded-xl bg-[#F6EAF5] p-5 flex gap-3" data-testid="hc-forgot-sent">
          <CheckCircle2 className="w-6 h-6 text-[#922A8C] shrink-0" />
          <p className="text-[#363636]">{sent} The link expires in 1 hour.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="hc-forgot-form">
          <HcInput icon={Mail} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required testId="hc-forgot-email" />
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <HcPrimaryButton type="submit" loading={loading} data-testid="hc-forgot-submit">Send Reset Link</HcPrimaryButton>
        </form>
      )}
      <Link to="/helpconnect/login" className="inline-block mt-6 text-[#922A8C] font-medium hover:underline" data-testid="hc-back-to-login">Back to sign in</Link>
    </HcAuthShell>
  );
}
